import React, { useEffect, useState } from "react";
import AsyncSelect from "react-select/async";
import moment from "moment";
import { callAPI } from "../utils/apiUtils";
import { apiUrls } from "../utils/apiUrls";
import { toast } from "react-toastify";

const BookingForm = ({ onBookingSuccess, initialDate = null, reservationId = null, onClose }) => {
  const isEditing = !!reservationId;
  const [records, setRecords] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(isEditing);

  const today = moment().format("YYYY-MM-DD");

  // Name, phone, date and time are mandatory. Date is pre-filled with today and the first
  // free time slot is pre-selected as a convenience, but both remain required fields -
  // anything else left blank (guests, etc.) gets a server-side default (1 guest).
  const [reservation, setReservation] = useState({
    customer_id: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    // Seeded from the calendar's per-day add button when it opened this form.
    date: initialDate || moment().format("YYYY-MM-DD"),
    time: "",
    guests: "",
    attendant_id: "",
    event: "Not Mentioned",
    message: ""
  });

  
  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        const resp = await callAPI(apiUrls.RESERVATION_RECIPIENTS, {}, "GET");
        if (resp.data) {
          setRecords(resp.data);
          if (resp.data.length > 0 && !isEditing) {
            setReservation(prev => ({
              ...prev,
              attendant_id: resp.data[0].id
            }));
          }
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pre-fill every field from the existing reservation when editing - the calendar's own event
  // payload only carries a formatted display shape (combined name, no email/message/event/
  // attendant_id), so a dedicated fetch is needed.
  useEffect(() => {
    if (!isEditing) return;

    const fetchDetails = async () => {
      try {
        setLoadingDetails(true);
        const resp = await callAPI(`${apiUrls.RESERVATION_GET_DETAILS}?id=${reservationId}`, {}, "GET");
        const data = resp.data && resp.data.data;
        if (data) {
          setReservation({
            customer_id: "",
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            email: data.email || "",
            phone: data.phone || "",
            date: data.date || "",
            time: data.time || "",
            guests: data.guests || "",
            attendant_id: data.attendant_id || "",
            event: data.event || "Not Mentioned",
            message: data.message || ""
          });
          if (data.time) {
            // Stored as 24h "HH:mm:ss" - the time-slot dropdown's own option values are the
            // restaurant's configured 12h "hh:mm a" slot labels, so this has to match that shape
            // to pre-select correctly once the slot list loads.
            setSelectedTime(moment(data.time, "HH:mm:ss").format("hh:mm a"));
          }
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load booking details");
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [isEditing, reservationId]);


  useEffect(() => {
    if (reservation.date) {
      fetchTimeSlots(reservation.date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservation.date]);

  const fetchTimeSlots = async (date) => {
    try {
      setLoadingTimes(true);
      const url = isEditing
        ? `${apiUrls.RESERVATION_TIME_SLOTS_AVAILABLE}?date=${date}&exclude_reservation_id=${reservationId}`
        : `${apiUrls.RESERVATION_LIST}?date=${date}`;
      const resp = await callAPI(url, {}, "GET");
      let slots = resp.data || [];

      if (isEditing && selectedTime && !slots.some(slot => slot.time === selectedTime)) {
        // The reservation's own slot is commonly missing from the "available" list simply because
        // it's already in the past relative to now (a same-day booking earlier today) or has since
        // been retired from the restaurant's manual time-slot config - keep it selectable
        // regardless, since it's still this booking's time. Put it first rather than appending it,
        // so it's immediately visible instead of buried after every other slot in the dropdown.
        slots = [{ time: selectedTime, status: "1" }, ...slots];
      }

      setTimeSlots(slots);

      if (!isEditing) {
        const firstValid = slots.find(slot => slot.status === "1");
        setSelectedTime(firstValid ? firstValid.time : "");
      }
    } catch (err) {
      console.error(err);
      setTimeSlots([]);
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setReservation(prev => ({
      ...prev,
      [name]: value
    }));
  };

  
const loadCustomers = async (inputValue) => {
  if (inputValue.length < 3) return [];
  try {
    const urlAutoComplete = `${apiUrls.CUSTOMER_AUTOCOMPLETE}?field=first_name&term=${inputValue}`;
    const resp = await callAPI(urlAutoComplete, {}, 'GET');

    if (resp.data && resp.data.data) {
      return resp.data.data.map(c => ({
        label: c.label,
        value: c.value,
        customerObject: c.customerObject
      }));
    }
  } catch (err) {
    console.error(err);
  }
  return [];
};


  const handleCustomerSelect = (option) => {
    if (!option || !option.customerObject) return;

    const c = option.customerObject;

    setReservation(prev => ({
      ...prev,
      customer_id: c.id,
      first_name: c.firstName || "",
      last_name: c.lastName || "",
      phone: c.contactNumber || "",
      email: c.email || ""
    }));
  };


  

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...reservation,
      // Time is now a required field (enforced by the <select required> above),
      // so selectedTime is always set by the time a submit reaches here.
      time: selectedTime ? moment(selectedTime, "h:mm:ss A").format("HH:mm:ss") : ""
    };

    try {
      const resp = isEditing
        ? await callAPI(apiUrls.RESERVATION_UPDATE_BOOKING, {}, "POST", { ...payload, id: reservationId }, {})
        : await callAPI(apiUrls.RESERVATION_CREATE, {}, "POST", payload, {});

      if (resp.data.errors) {
        setErrors(resp.data.errors);
      } else {
        setErrors({});
      }
      if (resp.data.status == 1) {
        toast.success(resp.data.message || (isEditing ? "Booking updated!" : "Reservation Booked!"));

        if (isEditing) {
          if (onClose) onClose();
        }
        if (onBookingSuccess) {
          onBookingSuccess(reservation.date);
        }
      }

    } catch (err) {
      console.error(err);
      // Validation failures come back as HTTP 422 with per-field errors.
      const data = err?.response?.data;
      if (data?.errors) {
        setErrors(data.errors);
        toast.error(data.message || "Please check the highlighted fields");
      } else {
        toast.error("Submission failed");
      }
    }
  };

  if (isEditing && loadingDetails) {
    return <div className="text-center p-4">Loading booking details...</div>;
  }

  return (

    <div>
{!isEditing && (
<div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-4">
  <div
    className="formStyle searchStyle tabSearchInput mbFullWd"
    style={{ width: "100%", position: "relative" }}
  >
    <svg
      width="17"
      height="18"
      className="searchIcon"
      style={{
        position: "absolute",
        top: "50%",
        left: "15px",
        transform: "translateY(-50%)",
        zIndex: 2,
      }}
    >
      <use xlinkHref="./images/sprite.svg#iconSearch"></use>
    </svg>

    <AsyncSelect
      cacheOptions
      loadOptions={loadCustomers}
      defaultOptions={false}
      placeholder="Search a customer (3+ chars)"
      onChange={handleCustomerSelect}
      classNamePrefix="react-select"
      styles={{
        control: (base, state) => ({
          ...base,
          backgroundColor: "rgb(41, 40, 40)",
          color: "#fff",
          border: `1px solid ${
            state.isFocused
              ? "var(--inputFocusBorder, #555)"
              : "var(--borderColor, #545353)"
          }`,
          borderRadius: "var(--bs-border-radius, 0.375rem)",
          minHeight: "48px",
          paddingLeft: "35px",
          boxShadow: state.isFocused
            ? "0 0 0 0.1rem rgba(255,255,255,0.1)"
            : "none",
          transition:
            "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
          cursor: "text",
          '&:hover': {
            borderColor: "var(--inputFocusBorder, #555)",
          },
        }),
        singleValue: (base) => ({
          ...base,
          color: "#fff",
        }),
        input: (base) => ({
          ...base,
          color: "#fff",
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: "rgb(41, 40, 40)",
          color: "#fff",
          border: "1px solid var(--borderColor, #545353)",
          borderRadius: "var(--bs-border-radius, 0.375rem)",
          marginTop: "4px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
          overflow: "hidden",
          zIndex: 5,
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused
            ? "rgba(255, 255, 255, 0.1)"
            : "rgb(41, 40, 40)",
          color: "#fff",
          cursor: "pointer",
        }),
        placeholder: (base) => ({
          ...base,
          color: "#AEA9B0",
          textTransform: "uppercase",
          fontSize: "13px",
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: "#ccc",
          paddingRight: "10px",
        }),
        indicatorSeparator: () => ({
          display: "none",
        }),
      }}

    />
  </div>
</div>
)}


      <div className="row g-3">
        <div className="col-lg-8">
          <div className="cardStyle">
            <form className="formStyle" onSubmit={handleSubmit}>
              <div className="row g-3">
                
                <div className="col-md-6">
                  <input type="text" name="first_name" className="form-control"
                    placeholder="First Name of the Guest *" value={reservation.first_name}
                    onChange={handleChange} required />
                  {errors.first_name && <div className="text-danger">{errors.first_name}</div>}
                </div>
                <div className="col-md-6">
                  <input type="text" name="last_name" className="form-control"
                    placeholder="Last Name (optional)" value={reservation.last_name}
                    onChange={handleChange} />
                  {errors.last_name && <div className="text-danger">{errors.last_name}</div>}
                </div>
                <div className="col-md-6">
                  <input type="email" name="email" className="form-control"
                    placeholder="Email Address (optional)" value={reservation.email}
                    onChange={handleChange} />
                  {errors.email && <div className="text-danger">{errors.email}</div>}
                </div>
                <div className="col-md-6">
                  <input type="tel" name="phone" className="form-control"
                    placeholder="Phone Number *" value={reservation.phone}
                    onChange={handleChange} required />
                  {(errors.phone || errors.mobile) && <div className="text-danger">{errors.phone || errors.mobile}</div>}
                </div>

                
                <div className="col-md-6">
                  <input type="date" name="date" className="form-control"
                    min={today} value={reservation.date}
                    onChange={handleChange} required />
                  {errors.date && <div className="text-danger">{errors.date}</div>}
                </div>

                <div className="col-md-6">
                  {loadingTimes ? (
                    <div>Loading time slots...</div>
                  ) : (
                    <select
                      className="form-select"
                      value={selectedTime}
                      onChange={(e) => setSelectedTime(e.target.value)}
                      required
                    >
                      <option value="">Select time *</option>
                      {timeSlots.length === 0 && <option disabled>No slots available</option>}
                      {timeSlots.map(slot => (
                        <option
                          key={slot.time}
                          value={slot.time}
                          disabled={slot.status !== 1 && slot.status !== "1"}
                        >
                          {(slot.status === 1 || slot.status === "1") ? slot.time : `${slot.time} - booked out`}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.time && <div className="text-danger">{errors.time}</div>}
                </div>

                
                <div className="col-md-6">
                  <input type="number" name="guests" className="form-control" placeholder="No. of Guests (optional)"
                    min="1" value={reservation.guests} onChange={handleChange} />
                  {errors.guests && <div className="text-danger">{errors.guests}</div>}
                </div>

                <div className="col-md-6">
                  <input type="text" name="message" className="form-control"
                    placeholder="Notes for Restaurant" value={reservation.message}
                    onChange={handleChange} />
                  {errors.message && <div className="text-danger">{errors.message}</div>}
                </div>
              </div>

              <div className="col-md-12 mt-3 d-flex gap-2">
                <button className="btn btnPrimary iconBtn" type="submit">
                  <span>{isEditing ? "Save Changes" : "Place Booking"}</span>
                </button>
                {isEditing && onClose && (
                  <button type="button" className="btn btnSecondary" onClick={onClose}>
                    <span>Cancel</span>
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>

        
        <div className="col-lg-4">
          <div className="cardStyle">
            <div className="formStyle mb-4">
              <select
                name="attendant_id"
                className="form-select"
                value={reservation.attendant_id}
                onChange={handleChange}
              >
                <option value="" disabled>Select a phone attendant</option>
                {records.map(rec => (
                  <option key={rec.id} value={rec.id}>
                    {rec.name || `${rec.first_name} ${rec.last_name}`}
                  </option>
                ))}
              </select>
              {errors.attendant_id && <div className="text-danger">{errors.attendant_id}</div>}
              <small className="mt-1 d-block">
                <a href="https://admin.orderart.com.au/staff" target="_blank" rel="noreferrer" className="primaryColor">
                  Click here
                </a> to add staff!
              </small>
            </div>

            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="event" value="Anniversary"
                checked={reservation.event === "Anniversary"} onChange={handleChange} />
              <label className="form-check-label">Anniversary</label>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="event" value="Birthday"
                checked={reservation.event === "Birthday"} onChange={handleChange} />
              <label className="form-check-label">Birthday</label>
            </div>
            <div className="form-check mb-2">
              <input className="form-check-input" type="radio" name="event" value="Not Mentioned"
                checked={reservation.event === "Not Mentioned"} onChange={handleChange} />
              <label className="form-check-label">Other</label>
            </div>
            {errors.event && <div className="text-danger">{errors.event}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingForm;
